import React, { useState, useEffect, useMemo } from "react";
import {
  StyleSheet,
  View,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { GroupModel } from "common247";
import { useFirebase } from "@/components/firebase/useFirebase";
import { getMyGroups } from "@/services/firebase/group-functions";
import { ThemedText } from "@/components/ThemedText";
import { ThemedInput } from "@/components/ThemedInput";
import { ThemedButton } from "@/components/ThemedButton";
import { ThemedPopupScreen } from "@/components/ThemedPopupScreen";
import { useThemeColor } from "@/hooks/useThemeColor";
import { useComponentTheme } from "@/hooks/useComponentTheme";
import type { IPopoverAnchor } from "@/utils/popover-positioning";

/**
 * Props for the GroupSelector popup component.
 */
export interface IGroupSelectorProps {
  /** Whether the group selector popup is visible */
  visible: boolean;
  /** Callback fired when the selector is dismissed */
  onClose: () => void;
  /** Optional contextual coordinates of cursor or trigger element */
  anchorPosition?: IPopoverAnchor | null;
  /** Single group selection callback */
  onSelectGroup?: (group: GroupModel) => void;
  /** Multi-group selection callback */
  onSelectMultipleGroups?: (groups: GroupModel[]) => void;
  /** Whether multi-select is enabled (default: false) */
  multiSelect?: boolean;
  /** Pre-selected group IDs */
  selectedGroupIds?: string[];
  /** Number of selected items being assigned to a group (e.g., in batch add context) */
  selectedCount?: number;
  /** Label describing the type of items being assigned (e.g., "contacts", "items") */
  itemTypeLabel?: string;
  /** Optional pre-fetched groups */
  groups?: GroupModel[];
  /** Optional title override (default: "Select Group") */
  title?: string;
  /** Optional subtitle or instructions */
  subtitle?: string;
  /** Target width for the popover card (default: 440) */
  width?: number;
  /** Target maximum height for the popover card (default: 520) */
  maxHeight?: number;
  /** Optional testID for testing */
  testID?: string;
}

const DEFAULT_EMPTY_IDS: string[] = [];

/**
 * Contextual GroupSelector powered by ThemedPopupScreen.
 * Provides searchable group selection anchored near the user cursor/trigger.
 */
export function GroupSelector({
  visible,
  onClose,
  anchorPosition,
  onSelectGroup,
  onSelectMultipleGroups,
  multiSelect = false,
  selectedGroupIds = DEFAULT_EMPTY_IDS,
  selectedCount,
  itemTypeLabel = "items",
  groups: propGroups,
  title = "Select Group",
  subtitle,
  width = 440,
  maxHeight = 500,
  testID = "group-selector",
}: IGroupSelectorProps) {
  const services = useFirebase();
  const [internalGroups, setInternalGroups] = useState<GroupModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>(selectedGroupIds);

  const { selector: selectorTheme, palette } = useComponentTheme();
  const cardBg = palette.card;
  const borderColor = selectorTheme.row.restingBorderColor;
  const hoverBorderColor = selectorTheme.row.hoverBorderColor;
  const tintColor = selectorTheme.row.selectedBorderColor;
  const textMuted = selectorTheme.row.subtitleColor;
  const badgeBg = selectorTheme.avatarBadge.backgroundColor;

  useEffect(() => {
    setSelectedIds((prev) => {
      if (
        prev.length === selectedGroupIds.length &&
        prev.every((id, idx) => id === selectedGroupIds[idx])
      ) {
        return prev;
      }
      return selectedGroupIds;
    });
  }, [selectedGroupIds, visible]);

  useEffect(() => {
    if (!visible) return;
    if (propGroups && propGroups.length > 0) {
      setInternalGroups(propGroups);
      return;
    }
    let isMounted = true;
    const fetchGroups = async () => {
      setLoading(true);
      try {
        const res = await getMyGroups(services);
        if (isMounted && res.status && res.groups) {
          setInternalGroups(res.groups);
        }
      } catch (err) {
        console.error("[GroupSelector] Failed to load groups:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchGroups();
    return () => {
      isMounted = false;
    };
  }, [visible, propGroups, services]);

  const filteredGroups = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return internalGroups;
    return internalGroups.filter((g) => {
      const name = (g.name || "").toLowerCase();
      const desc = (g.description || "").toLowerCase();
      return name.includes(q) || desc.includes(q);
    });
  }, [internalGroups, searchQuery]);

  const handleToggleGroup = (group: GroupModel) => {
    const id = group.id || group.groupId;
    if (!id) return;

    if (!multiSelect) {
      onSelectGroup?.(group);
      onClose();
      return;
    }

    const nextSelected = selectedIds.includes(id)
      ? selectedIds.filter((item) => item !== id)
      : [...selectedIds, id];
    setSelectedIds(nextSelected);

    if (onSelectMultipleGroups) {
      const selectedModels = internalGroups.filter((g) =>
        nextSelected.includes(g.id || g.groupId || "")
      );
      onSelectMultipleGroups(selectedModels);
    }
  };

  const getMemberCount = (group: GroupModel): number => {
    if (group.members) {
      return Array.isArray(group.members)
        ? group.members.length
        : Object.keys(group.members).length;
    }
    return 0;
  };

  const computedSubtitle =
    subtitle ??
    (multiSelect
      ? `${selectedIds.length} group${selectedIds.length === 1 ? "" : "s"} selected`
      : selectedCount != null && selectedCount > 0
        ? `Choose a group to receive ${selectedCount} selected ${itemTypeLabel}:`
        : undefined);

  return (
    <ThemedPopupScreen
      visible={visible}
      onClose={onClose}
      anchorPosition={anchorPosition}
      title={title}
      subtitle={computedSubtitle}
      icon="people-circle-outline"
      width={width}
      maxHeight={maxHeight}
      testID={testID}
      footer={
        multiSelect ? (
          <View style={styles.footerRow}>
            <ThemedButton
              title="Done"
              variant="primary"
              compact
              onPress={onClose}
              testID={`${testID}-done-btn`}
            />
          </View>
        ) : undefined
      }
    >
      <View style={styles.container}>
        {/* Search input */}
        <View style={styles.searchRow}>
          <ThemedInput
            value={searchQuery}
            onChangeText={setSearchQuery}
            placeholder="Search groups..."
            style={styles.searchInput}
            testID={`${testID}-search-input`}
          />
        </View>

        {/* Content list */}
        {loading ? (
          <View style={styles.centerState}>
            <ActivityIndicator size="small" color={tintColor} />
            <ThemedText style={[styles.stateText, { color: textMuted }]}>
              Loading groups...
            </ThemedText>
          </View>
        ) : filteredGroups.length === 0 ? (
          <View style={styles.centerState}>
            <Ionicons name="people-circle-outline" size={32} color={textMuted} />
            <ThemedText style={[styles.stateText, { color: textMuted }]}>
              {searchQuery ? "No matching groups" : "No groups available"}
            </ThemedText>
          </View>
        ) : (
          <ScrollView
            style={styles.groupList}
            contentContainerStyle={styles.listContent}
            showsVerticalScrollIndicator={true}
            nestedScrollEnabled={true}
            keyboardShouldPersistTaps="handled"
          >
            {filteredGroups.map((group) => {
              const id = group.id || group.groupId || "";
              const isSelected = selectedIds.includes(id);
              const count = getMemberCount(group);

              return (
                <TouchableOpacity
                  key={id}
                  style={[
                    styles.groupRow,
                    {
                      borderColor,
                      backgroundColor: isSelected
                        ? selectorTheme.row.selectedBg
                        : selectorTheme.row.restingBg,
                    },
                    isSelected && [styles.selectedRow, { borderColor: tintColor }],
                  ]}
                  onPress={() => handleToggleGroup(group)}
                  activeOpacity={0.7}
                  testID={`${testID}-item-${id}`}
                >
                  <View style={[styles.iconBadge, { backgroundColor: badgeBg }]}>
                    <Ionicons name="people" size={18} color={selectorTheme.avatarBadge.iconColor || palette.text} />
                  </View>

                  <View style={styles.infoCol}>
                    <ThemedText type="defaultSemiBold" style={[styles.groupName, { color: selectorTheme.row.titleColor }]} numberOfLines={1}>
                      {group.name || "Unnamed Group"}
                    </ThemedText>
                    {group.description ? (
                      <ThemedText style={[styles.groupDescription, { color: textMuted }]} numberOfLines={1}>
                        {group.description}
                      </ThemedText>
                    ) : null}
                  </View>

                  <View style={styles.badgeCol}>
                    <View style={[styles.countBadge, { backgroundColor: badgeBg }]}>
                      <Ionicons name="person" size={10} color={textMuted} style={{ marginRight: 3 }} />
                      <ThemedText style={[styles.countText, { color: textMuted }]}>
                        {count}
                      </ThemedText>
                    </View>
                    {isSelected && (
                      <Ionicons name="checkmark-circle" size={18} color={tintColor} style={{ marginLeft: 6 }} />
                    )}
                  </View>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        )}
      </View>
    </ThemedPopupScreen>
  );
}

export default GroupSelector;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minHeight: 240,
  },
  searchRow: {
    paddingHorizontal: 8,
    paddingVertical: 4,
  },
  searchInput: {
    height: 32,
    fontSize: 12,
  },
  groupList: {
    flex: 1,
  },
  listContent: {
    paddingHorizontal: 8,
    paddingBottom: 6,
    gap: 4,
  },
  groupRow: {
    flexDirection: "row",
    alignItems: "center",
    padding: 6,
    borderRadius: 6,
    borderWidth: 1,
  },
  selectedRow: {
    borderWidth: 1.5,
  },
  iconBadge: {
    width: 28,
    height: 28,
    borderRadius: 6,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 8,
  },
  infoCol: {
    flex: 1,
    marginRight: 8,
  },
  groupName: {
    fontSize: 13,
  },
  groupDescription: {
    fontSize: 11,
    marginTop: 1,
  },
  badgeCol: {
    flexDirection: "row",
    alignItems: "center",
  },
  countBadge: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 5,
    paddingVertical: 2,
    borderRadius: 4,
  },
  countText: {
    fontSize: 10,
  },
  centerState: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 24,
    gap: 6,
  },
  stateText: {
    fontSize: 12,
  },
  footerRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
  },
});

